import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Submits a product form to /cart/add.js directly and morphs every
 * cart-items-component section on the page with the fresh HTML, instead of
 * relying on <product-form-component>'s own submit handling. That component
 * dispatches the update event from inside the very cart-items-component
 * subtree it needs to refresh, which isn't a scenario the rest of the theme
 * exercises - going straight to fetch + morphSection here is easy to reason
 * about and matches the (working) pattern used for the gift line's own
 * property edits.
 *
 * @param {HTMLFormElement} form
 * @param {string | undefined} currentSectionId
 * @param {{ onSuccess?: () => void }} [callbacks]
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
async function addProductToCart(form, currentSectionId, { onSuccess } = {}) {
  const formData = new FormData(form);

  const sectionsToUpdate = new Set([currentSectionId].filter(Boolean));
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });
  formData.append('sections', Array.from(sectionsToUpdate).join(','));
  formData.append('sections_url', window.location.pathname);

  const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(formData.get('id') ?? ''), quantity: Number(formData.get('quantity')) || 1 }],
      promise: deferredUpdatePromise.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
    const data = await response.json();
    console.log('[gift-form] /cart/add.js responded', {
      status: response.status,
      data,
      requestedSections: Array.from(sectionsToUpdate),
    });

    if (data.status) {
      const errorMessage = data.message || data.description || 'Failed to add to cart';
      console.error('[gift-form] add to cart returned an error', data);
      deferredUpdatePromise.reject(new Error(errorMessage));
      form.dispatchEvent(new CartErrorEvent({ error: errorMessage, code: 'INVALID' }));
      return { ok: false, error: errorMessage };
    }

    if (!data.sections) {
      console.error('[gift-form] response has no "sections" payload - was `sections`/`sections_url` sent?', data);
    }

    for (const sectionId of sectionsToUpdate) {
      if (data.sections?.[sectionId]) {
        console.log('[gift-form] morphing section', sectionId);
        await morphSection(sectionId, data.sections[sectionId], { mode: 'full' });
        console.log('[gift-form] morphed section', sectionId);
      } else {
        console.error('[gift-form] no HTML in response for section', sectionId, data.sections);
      }
    }

    let cart;
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      cart = await cartResponse.json();
    } catch {
      cart = undefined;
    }

    deferredUpdatePromise.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
      detail: { sections: data.sections, source: 'gift-form', didError: false },
    });

    onSuccess?.();
    return { ok: true };
  } catch (error) {
    console.error('[gift-form] addProductToCart threw', error);
    deferredUpdatePromise.reject(error);
    form.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error while adding to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    return { ok: false, error: error?.message };
  }
}

/**
 * Mirrors the From/To/Message onto the cart-level attributes the previous theme used
 * (`gift-from`/`gift-to`/`gift-note`, with the "From: "/"To: " label baked into the value),
 * since downstream systems (warehouse/fulfillment) read the gift info from there rather than
 * from this theme's own `_gift_from`/`_gift_to`/`_gift_message` line-item properties. Pass
 * empty strings to clear them (e.g. when the gift line is removed).
 * @param {string} from
 * @param {string} to
 * @param {string} message
 */
async function syncLegacyGiftCartAttributes(from, to, message) {
  const attributes = {
    'gift-from': from ? `From: ${from}` : '',
    'gift-to': to ? `To: ${to}` : '',
    'gift-note': message || '',
  };

  try {
    await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ attributes }) })
    );
  } catch (error) {
    console.error('[gift-form] failed to sync legacy cart attributes', error);
  }
}

/**
 * Renders the "Is this a gift?" form on the cart page: an optional From/To/Message
 * set of hidden line-item properties attached to a merchant-configured gift product.
 *
 * Two states:
 * - No gift line in the cart yet: the fields are editable; submitting adds the gift
 *   product with the field values as hidden properties (see addGift below).
 * - A gift line already exists: fields render disabled/locked (server-rendered), with
 *   Edit/Remove actions. Edit re-enables the fields for a Save/Cancel pass that PATCHes
 *   the existing line's properties via /cart/change.js, since Shopify's cart AJAX API
 *   has no endpoint for editing properties other than change-by-line.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} fromInput
 * @property {HTMLInputElement} toInput
 * @property {HTMLTextAreaElement} messageInput
 * @property {HTMLElement} [charCount]
 * @property {HTMLElement} addActions
 * @property {HTMLElement} lockedActions
 * @property {HTMLElement} editActions
 * @property {HTMLButtonElement} [addButton]
 * @property {HTMLElement} [errorMessage]
 *
 * @extends {Component<Refs>}
 */
class GiftDetailsForm extends Component {
  requiredRefs = ['fromInput', 'toInput', 'messageInput', 'addActions', 'lockedActions', 'editActions'];

  connectedCallback() {
    super.connectedCallback();
    this.#storeCommittedValues();
    this.#updateCharCount();
  }

  /**
   * Keep the character counter in sync as the customer types.
   */
  updateCharCount() {
    this.#updateCharCount();
  }

  /**
   * Adds the gift product to the cart with the current field values as hidden properties.
   * @param {SubmitEvent} event
   */
  async addGift(event) {
    event.preventDefault();
    // Not event.target: the declarative on:submit binding proxies the event so
    // .target resolves to this component (see assets/component.js), not the
    // native <form>. Query it directly, matching product-form.js's own pattern.
    const form = this.querySelector('form');
    console.log('[gift-form] addGift invoked', { form });
    if (!form) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#clearError();
    const { addButton } = this.refs;
    if (addButton) addButton.disabled = true;

    // The attributes have to land before the add, not after: adding the product triggers a
    // section re-render (showing the locked/edit view) that reads these same cart attributes to
    // pre-fill the fields, so if they're set afterwards that render only ever sees stale (blank)
    // values even though the attributes themselves did save correctly server-side.
    await syncLegacyGiftCartAttributes(
      this.refs.fromInput.value,
      this.refs.toInput.value,
      this.refs.messageInput.value
    );

    const result = await addProductToCart(form, this.dataset.sectionId);

    if (!result.ok) {
      this.#showError(result.error);
      if (addButton) addButton.disabled = false;
    }
    // On success, the section morph re-renders this whole component from fresh
    // server state (now showing the locked/edit view) - nothing else to do here.
  }

  /**
   * Switches the locked fields into an editable state.
   */
  enableEditing() {
    this.#setFieldsDisabled(false);
    this.refs.lockedActions.hidden = true;
    this.refs.editActions.hidden = false;
    this.refs.fromInput.focus();
  }

  /**
   * Reverts to the last saved values and re-locks the fields, without a network call.
   */
  cancelEditing() {
    const committed = this.#committedValues;
    this.refs.fromInput.value = committed.from;
    this.refs.toInput.value = committed.to;
    this.refs.messageInput.value = committed.message;
    this.#updateCharCount();
    this.#setFieldsDisabled(true);
    this.refs.editActions.hidden = true;
    this.refs.lockedActions.hidden = false;
    this.#clearError();
  }

  /**
   * Saves the current field values as cart attributes, keeping the "_itemType" marker on the
   * existing gift cart line (From/To/Message themselves are cart attributes, not line-item
   * properties - see syncLegacyGiftCartAttributes).
   */
  async saveProperties() {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.#clearError();
    this.setAttribute('saving', '');

    // Same reasoning as addGift: set the attributes before the line update below, since that
    // update's own section re-render is what actually displays them.
    await syncLegacyGiftCartAttributes(
      this.refs.fromInput.value,
      this.refs.toInput.value,
      this.refs.messageInput.value
    );

    const properties = {
      // /cart/change.js replaces the whole properties object for the line, so this has to be
      // resent here too or editing would silently drop it.
      _itemType: 'gift',
    };

    const sectionsToUpdate = new Set([this.dataset.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: lineKey,
      properties,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: lineKey, quantity: 1 }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const responseText = await response.text();
      const data = JSON.parse(responseText);

      if (data.errors) {
        const errorMessage = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
        deferredUpdatePromise.reject(new Error(errorMessage));
        this.dispatchEvent(new CartErrorEvent({ error: errorMessage, code: 'INVALID' }));
        this.#showError(errorMessage);
        return;
      }

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          source: 'gift-details-form',
          didError: false,
        },
      });

      for (const sectionId of sectionsToUpdate) {
        if (data.sections[sectionId]) {
          await morphSection(sectionId, data.sections[sectionId], { mode: 'full' });
        }
      }
    } catch (error) {
      deferredUpdatePromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Failed to update cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#showError(error?.message);
    } finally {
      resetShimmer(this);
      this.removeAttribute('saving');
    }
  }

  /**
   * Removes the gift line and clears the legacy cart-level gift attributes, then delegates the
   * actual line removal to cart-items-component (the theme's own generic cart-item removal).
   */
  async removeGift() {
    await syncLegacyGiftCartAttributes('', '', '');

    const line = Number(this.dataset.lineIndex);
    if (!line) return;

    const cartItemsComponent = /** @type {import('./component-cart-items').CartItemsComponent | null} */ (
      document.querySelector('cart-items-component')
    );
    if (cartItemsComponent && typeof cartItemsComponent.onLineItemRemove === 'function') {
      cartItemsComponent.onLineItemRemove(line);
    }
  }

  /** @type {{ from: string, to: string, message: string }} */
  #committedValues = { from: '', to: '', message: '' };

  #storeCommittedValues() {
    this.#committedValues = {
      from: this.refs.fromInput.value,
      to: this.refs.toInput.value,
      message: this.refs.messageInput.value,
    };
  }

  /**
   * @param {boolean} disabled
   */
  #setFieldsDisabled(disabled) {
    this.refs.fromInput.disabled = disabled;
    this.refs.toInput.disabled = disabled;
    this.refs.messageInput.disabled = disabled;
  }

  #updateCharCount() {
    const { charCount, messageInput } = this.refs;
    if (!charCount) return;

    const max = charCount.getAttribute('data-max') ?? '';
    charCount.textContent = `${messageInput.value.length} / ${max}`;
  }

  /**
   * @param {string} [message]
   */
  #showError(message) {
    if (!this.refs.errorMessage) return;
    this.refs.errorMessage.textContent = message || '';
    this.refs.errorMessage.hidden = !message;
  }

  #clearError() {
    this.#showError('');
  }
}

if (!customElements.get('gift-details-form')) {
  customElements.define('gift-details-form', GiftDetailsForm);
}

/**
 * A single "gift bag" product row inside the modal. Adds itself to the cart directly
 * (see addProductToCart above) and closes the enclosing dialog once the add succeeds.
 *
 * @extends {Component}
 */
class GiftBagItem extends Component {
  /**
   * @param {SubmitEvent} event
   */
  async addToCart(event) {
    event.preventDefault();
    // Not event.target - see the comment in GiftDetailsForm.addGift above.
    const form = this.querySelector('form');
    console.log('[gift-form] addToCart invoked', { form });
    if (!form) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Captured before the morph, in case this row's own markup gets replaced
    // (it switches to the "already in cart" branch once the add succeeds).
    const dialogComponent = this.closest('dialog-component');

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    const result = await addProductToCart(form, this.dataset.sectionId, {
      onSuccess: () => {
        if (dialogComponent && 'closeDialog' in dialogComponent && typeof dialogComponent.closeDialog === 'function') {
          dialogComponent.closeDialog();
        }
      },
    });

    if (!result.ok && submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = false;
    }
  }
}

if (!customElements.get('gift-bag-item')) {
  customElements.define('gift-bag-item', GiftBagItem);
}
